import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import useModal from './useModal';
import { errorMessage, customMessage } from 'actions/internal';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PartnerCustomTagField from '../PartnerCustomTagField';
import SupervisorField from '../SupervisorField';
const CohortForm = ({group, subgroup, cohort, onCustomMessage, onErrorMessage, auth}) => {
  const { t } = useTranslation();
  const formMethods = useForm({
    defaultValues: {
      group_id: group.id,
      subgroup_id: subgroup.id,
      name: cohort ? cohort.name : '',
      code: cohort ? cohort.code : '',
      asq_responsible_emails: cohort ? cohort.asq_responsible_emails : '',
      partner_custom_tag_association: cohort ? cohort.partner_custom_tag_association : null,
      supervisor_id: cohort ? cohort.supervisor_id : null,
    },
  });

  const { register, formState: { errors }, setError, handleSubmit, clearErrors } = formMethods;
  const cohort_id = cohort ? cohort.id : null;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (cohort) => {
      if (cohort_id) {
        return axios.put(Routes.api_dashboard_administration_organizations_update_cohort_path({
          cohort_id: cohort_id,
        }), { cohort }, { headers: authHeaders() })
      } else {
        return axios.post(Routes.api_dashboard_administration_organizations_create_cohort_path(),
          { cohort }, { headers: authHeaders() })
      }
    },
    onSuccess: () => {
      onCustomMessage(t('administration.cohortForm.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['administration', 'organization'] }).then(() => {
        closeModal();
      })
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage(t('errors.generic'));
        return
      }

      if (error.response.status === 422) {
        onErrorMessage(t('errors.formValidation'));

        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          setError(key, {
            type: 'manual',
            message: errors[key].join(', '),
          });
        });

        return
      }
    },
  })

  // Determines if the partner custom tag field should be visible:
  // for new cohorts: only visible if the parent group and parent subgroup don't have a tag
  // for existing cohorts: visible if the parent group and parent subgroup don't have a tag
  // This prevents tag conflicts in the hierarchy (parent-child relationships)
  const isPartnerCustomTagAssociationVisible = !group.partner_custom_tag_association && !subgroup.partner_custom_tag_association

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit((data) => {
        clearErrors()

        // rename the key partner_custom_tag_association to partner_custom_tag_association_attributes
        data.partner_custom_tag_association_attributes = data.partner_custom_tag_association
        delete data.partner_custom_tag_association

        // if the partner_custom_tag_association_attributes._destroy is true, then we need to delete the partner_custom_tag_association
        if (data.partner_custom_tag_association_attributes?._destroy) {
          data.partner_custom_tag_association_attributes = {
            ...cohort.partner_custom_tag_association,
            _destroy: true,
          }
        }

        save.mutate(data)
      })}>
        <div className="row">
          <div className="column">
            <div className="column small-12">
              <div className='o-card__wrap o-admin-card'>
                <div className='u-flex u-flex-big-gap'>
                  <div className='u-flex-1'>
                    {cohort ? <h3><b>{t('administration.cohortForm.editTitle')}</b></h3> : <h3><b>{t('administration.cohortForm.newTitle')}</b></h3>}
                  </div>
                </div>
                <div className='u-margin-top--base'>
                  <input type='hidden' {...register('group_id')} />
                  <input type='hidden' {...register('subgroup_id')} />
                  <p>
                    <span className='u-txt--label'>{t('administration.cohortForm.name')}</span><br />
                    <input type='text' {...register('name')} aria-invalid={errors.name ? 'true' : 'false'} />
                    {errors.name && <p className='u-txt--error'>{errors.name.message}</p>}
                  </p>
                  <p>
                    <span className='u-txt--label'>{t('administration.cohortForm.code')}</span><br />
                    <input type='text' {...register('code')} aria-invalid={errors.code ? 'true' : 'false'} />
                    {errors.code && <p className='u-txt--error'>{errors.code.message}</p>}
                  </p>
                  <p>
                    <span className='u-txt--label'>{t('administration.cohortForm.contactEmails')}</span>
                    <br />
                    <span className='u-txt--label'>{t('administration.cohortForm.contactEmailsHelp')}</span>
                    <br />
                    <input type='text' {...register('asq_responsible_emails')} aria-invalid={errors.asq_responsible_emails ? 'true' : 'false'} />
                    {errors.asq_responsible_emails && <p className='u-txt--error'>{errors.asq_responsible_emails.message}</p>}
                  </p>
                  {auth.isMaxPermissions && (
                    <>
                      {cohort && (
                        <p>
                          <SupervisorField cohortId={cohort.id} />
                        </p>
                      )}
                      {isPartnerCustomTagAssociationVisible && (
                        <p>
                          <PartnerCustomTagField kind="organization_type" label={t('administration.organizationType')} />
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div className='row'>
                  <div className='column small-12'>
                    <input type='submit' className='o-sp-button o-sp-button--update o-sp-button--primary' value={t('actions.save')} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

CohortForm.propTypes = {
  group: PropTypes.object.isRequired,
  subgroup: PropTypes.object.isRequired,
  cohort: PropTypes.object,
  onCustomMessage: PropTypes.func.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, type) => {
      dispatch(customMessage(message, type))
    },
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(CohortForm);

